import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import ArrowBackIosOutlined from '@mui/icons-material/ArrowBackIosOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import ManageAccountsOutlined from '@mui/icons-material/ManageAccountsOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import {
  Avatar,
  Badge,
  Box,
  ButtonBase,
  IconButton as MuiIconButton,
  ListItemIcon,
  ListItemText,
  Menu as MuiMenu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useProfilePicture, useUser } from '@si/hooks';
import { useTranslate } from '@si/utils';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, type Location, useLocation, useNavigate } from 'react-router';

import { useSalesDemoMode } from '../../features/sales-demo/salesDemoMode';
import { IconButton } from '../IconButton/IconButton';
import { Logo } from '../Logo/Logo';
import { Stack } from '../Stack/Stack';
import { useThemeMode } from '../ThemeProvider';
import { glassSurface } from './glassSurface';
import { scrollToHeaderTarget, useHeaderContext } from './HeaderContext';
import { ToMainContent } from './ToMainContent';

const SalesDemoSwitch = lazy(() =>
  import('../../features/sales-demo/SalesDemoSwitch').then((m) => ({ default: m.SalesDemoSwitch }))
);

const inIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();
const logoSize = '28px';
const profileControlSize = '2.25rem';
const isSettingsPath = (pathname: string) => /^\/(admin|profile)(\/|$)/.test(pathname);
interface Props {
  hasUnreadLearningContent?: boolean;
  onLearningCenterOpen?: () => void;
}

export function NavBar({ hasUnreadLearningContent = false, onLearningCenterOpen }: Props) {
  const { mode, toggleMode } = useThemeMode();
  const { t, translate } = useTranslate();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const { user, canUseSalesDemo } = useUser();
  const salesMode = useSalesDemoMode();
  const { profilePictureUrl } = useProfilePicture(!!user?.profilePictureUrl);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const isSettingsPage = isSettingsPath(pathname);
  const returnLocationRef = useRef<Location | null>(null);
  const returnScrollRef = useRef({ left: 0, top: 0 });
  const restoreReturnScroll = useRef(false);
  useEffect(() => {
    if (!isSettingsPage) {
      returnLocationRef.current = location;
    } else if (!returnLocationRef.current) {
      const returnTo = (location.state as { returnTo?: Location } | null)?.returnTo;
      if (returnTo?.pathname?.startsWith('/') && !isSettingsPath(returnTo.pathname)) {
        returnLocationRef.current = returnTo;
        returnScrollRef.current = location.state?.returnScroll ?? { left: 0, top: 0 };
      }
    }
  }, [location, isSettingsPage]);
  useEffect(() => {
    if (isSettingsPage || !restoreReturnScroll.current) return;
    restoreReturnScroll.current = false;
    const position = returnScrollRef.current;
    let complete = false;
    let observer: ResizeObserver | undefined;
    const restore = () => {
      if (complete) return;
      window.scrollTo({ ...position, behavior: 'instant' });
      if (
        Math.abs(window.scrollY - position.top) < 1 &&
        Math.abs(window.scrollX - position.left) < 1
      ) {
        complete = true;
        observer?.disconnect();
      }
    };
    restore();
    // A lazy-loaded destination can initially be too short for the saved position.
    if (!complete && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(restore);
      observer.observe(document.body);
    }
    const frame = requestAnimationFrame(restore);
    const timeout = window.setTimeout(() => {
      complete = true;
      observer?.disconnect();
    }, 2000);
    return () => {
      complete = true;
      observer?.disconnect();
      cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [location, isSettingsPage]);
  const openSettings = (path: '/profile' | '/admin') => {
    if (!isSettingsPage) returnScrollRef.current = { left: window.scrollX, top: window.scrollY };
    navigate(path, {
      state: { returnTo: returnLocationRef.current, returnScroll: returnScrollRef.current },
    });
  };
  const returnToWorkspace = () => {
    restoreReturnScroll.current = true;
    const target = returnLocationRef.current;
    navigate(
      target
        ? { pathname: target.pathname, search: target.search, hash: target.hash }
        : '/projects',
      { state: target?.state, replace: true }
    );
  };
  const showNavigationLinks = !inIframe;
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<HTMLElement | null>(null);
  const nextThemeLabel = mode === 'dark' ? translate(t.light) : translate(t.dark);
  const headerRef = useRef<HTMLElement>(null);
  const context = useHeaderContext();
  const setHeight = context?.setHeight;
  const height = context?.height ?? 44;
  const project =
    context?.project &&
    (pathname === `/projects/${context.project.id}` ||
      pathname.startsWith(`/projects/${context.project.id}/`))
      ? context.project
      : null;
  const installation = context?.installation?.pathname === pathname ? context.installation : null;
  const hasPageContext =
    (project && showNavigationLinks) ||
    pathname.startsWith('/admin') ||
    pathname.startsWith('/profile');
  const brand = (
    <Stack direction="row" flexWrap="nowrap" alignItems="center" gap="0.75rem">
      <Logo
        variant="logoSmall"
        width={compact ? '20px' : logoSize}
        placeholderHeight={compact ? '20px' : logoSize}
      />
      {!hasPageContext && (
        <Typography
          component="span"
          variant="h1"
          fontSize="0.875rem"
          fontWeight={500}
          lineHeight="1.25rem"
          color="text.primary"
          sx={(theme) => ({
            whiteSpace: 'nowrap',
            paddingLeft: '0.75rem',
            borderLeft: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          })}
        >
          into® Control System
        </Typography>
      )}
    </Stack>
  );

  useEffect(() => {
    const header = headerRef.current;
    if (!header || !setHeight) return;
    const measure = () => {
      const measured = header.getBoundingClientRect().height;
      if (measured > 0) setHeight(measured);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [setHeight]);

  return (
    <Box
      component="header"
      ref={headerRef}
      sx={(theme) => ({
        ...glassSurface(theme),
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        flexShrink: 0,
      })}
    >
      <nav aria-label="Main">
        <ToMainContent />
        <Stack
          direction="row"
          flexWrap={compact ? 'wrap' : 'nowrap'}
          alignItems="center"
          gap={compact ? '0.5rem' : '0.75rem'}
          py="0.25rem"
          sx={{ paddingInline: 'var(--app-content-padding-inline, 2rem)' }}
        >
          {showNavigationLinks ? (
            <Tooltip title={translate(t.allProjects)}>
              <Box
                component={Link}
                to="/projects"
                aria-label={
                  hasPageContext
                    ? translate(t.allProjects)
                    : `into® Control System — ${translate(t.allProjects)}`
                }
                sx={{
                  display: 'flex',
                  flexShrink: 0,
                  borderRadius: '0.25rem',
                  textDecoration: 'none',
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                }}
              >
                {brand}
              </Box>
            </Tooltip>
          ) : (
            brand
          )}
          {isSettingsPage && (
            <Tooltip title={translate(t.back)}>
              <MuiIconButton
                aria-label={translate(t.back)}
                onClick={returnToWorkspace}
                size="small"
                sx={{ flexShrink: 0, color: 'text.secondary' }}
              >
                <ArrowBackIosOutlined fontSize="small" />
              </MuiIconButton>
            </Tooltip>
          )}
          <Stack
            direction="row"
            flexWrap="nowrap"
            alignItems="center"
            gap="0.125rem"
            minWidth={0}
            flex={1}
            sx={{
              display: hasPageContext ? 'flex' : 'none',
              order: compact && !isSettingsPage ? 1 : 0,
              flexBasis: compact && !isSettingsPage ? '100%' : 'auto',
            }}
          >
            {project && showNavigationLinks ? (
              <Stack
                direction="row"
                flexWrap="nowrap"
                alignItems="center"
                minWidth={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'surface',
                  borderRadius: '0.5rem',
                  p: '0.125rem',
                  maxWidth: '100%',
                }}
              >
                <Tooltip title={translate(t.allProjects)}>
                  <ButtonBase
                    component={Link}
                    to="/projects"
                    aria-label={compact ? translate(t.allProjects) : undefined}
                    sx={{
                      flexShrink: 0,
                      px: compact ? '0.375rem' : '0.5rem',
                      py: '0.25rem',
                      borderRadius: '0.375rem',
                      '&:hover': { bgcolor: 'action.hover' },
                      '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
                    }}
                  >
                    {compact ? (
                      <HomeOutlined fontSize="small" />
                    ) : (
                      <Typography fontSize="0.875rem">{translate(t.projects)}</Typography>
                    )}
                  </ButtonBase>
                </Tooltip>
                <ChevronRight fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                <ButtonBase
                  title={translate(t.projectOverview)}
                  onClick={() => scrollToHeaderTarget(project.root, height)}
                  sx={{
                    minWidth: 0,
                    maxWidth: { xs: '10rem', sm: '18rem', lg: '26rem' },
                    px: '0.75rem',
                    py: '0.25rem',
                    gap: '0.25rem',
                    borderRadius: '0.375rem',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                >
                  <Typography noWrap fontSize="0.875rem" fontWeight={500}>
                    {project.name}
                  </Typography>
                </ButtonBase>
                {project.collapsed && (
                  <>
                    <ChevronRight
                      fontSize="small"
                      sx={{ color: 'text.secondary', flexShrink: 0 }}
                    />
                    <ButtonBase
                      onClick={() => scrollToHeaderTarget(project.modules, height)}
                      title={translate(t.scrollToNavigation)}
                      sx={{
                        px: '0.625rem',
                        py: '0.25rem',
                        minWidth: 0,
                        borderRadius: '0.375rem',
                        '&.Mui-focusVisible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                        },
                      }}
                    >
                      <Typography noWrap fontSize="0.875rem" color="text.secondary">
                        {project.module}
                      </Typography>
                    </ButtonBase>
                    {installation?.collapsed && !compact && (
                      <>
                        <ChevronRight
                          fontSize="small"
                          sx={{ color: 'text.secondary', flexShrink: 0 }}
                        />
                        <ButtonBase
                          onClick={() => scrollToHeaderTarget(installation.element, height)}
                          title={translate(t.scrollToNavigation)}
                          sx={{
                            px: '0.625rem',
                            py: '0.25rem',
                            maxWidth: '14rem',
                            minWidth: 0,
                            borderRadius: '0.375rem',
                            '&.Mui-focusVisible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                            },
                          }}
                        >
                          <Typography noWrap fontSize="0.875rem" color="text.secondary">
                            {installation.name}
                          </Typography>
                        </ButtonBase>
                      </>
                    )}
                  </>
                )}
              </Stack>
            ) : pathname.startsWith('/admin') ? (
              <Typography noWrap fontSize="0.875rem">
                {translate(t.adminPanel)}
              </Typography>
            ) : pathname.startsWith('/profile') ? (
              <Typography noWrap fontSize="0.875rem">
                {translate(t.profileSettings)}
              </Typography>
            ) : null}
          </Stack>
          <Stack
            direction="row"
            flexWrap="nowrap"
            gap="0.75rem"
            alignItems="center"
            flexShrink={0}
            sx={{ ml: 'auto' }}
          >
            {onLearningCenterOpen && (
              <Badge
                color="primary"
                invisible={!hasUnreadLearningContent}
                overlap="circular"
                variant="dot"
              >
                <IconButton
                  size="small"
                  ariaLabel={translate(t.learningCenter)}
                  icon={SchoolOutlined}
                  onClick={onLearningCenterOpen}
                />
              </Badge>
            )}
            <IconButton
              size="small"
              ariaLabel={`${translate(t.themeMode)}: ${nextThemeLabel}`}
              icon={mode === 'dark' ? LightModeOutlined : DarkModeOutlined}
              onClick={toggleMode}
            />
            <MuiIconButton
              aria-label={translate(t.profile)}
              aria-haspopup="menu"
              aria-controls={profileMenuAnchor ? 'profile-menu' : undefined}
              aria-expanded={!!profileMenuAnchor}
              onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
              sx={{
                width: profileControlSize,
                height: profileControlSize,
              }}
            >
              {profilePictureUrl ? (
                <Avatar src={profilePictureUrl} sx={{ width: 28, height: 28 }} />
              ) : (
                <AccountCircleOutlined />
              )}
            </MuiIconButton>
            <MuiMenu
              id="profile-menu"
              anchorEl={profileMenuAnchor}
              open={!!profileMenuAnchor}
              onClose={() => setProfileMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {!salesMode && (
                <MenuItem
                  onClick={() => {
                    setProfileMenuAnchor(null);
                    openSettings('/profile');
                  }}
                >
                  <ListItemIcon>
                    <ManageAccountsOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{translate(t.profileSettings)}</ListItemText>
                </MenuItem>
              )}
              {user?.isSiAdmin && showNavigationLinks && (
                <MenuItem
                  onClick={() => {
                    setProfileMenuAnchor(null);
                    openSettings('/admin');
                  }}
                >
                  <ListItemIcon>
                    <AdminPanelSettingsOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{translate(t.adminPanel)}</ListItemText>
                </MenuItem>
              )}
              {canUseSalesDemo && (
                <Suspense fallback={null}>
                  <SalesDemoSwitch />
                </Suspense>
              )}
            </MuiMenu>
          </Stack>
        </Stack>
      </nav>
    </Box>
  );
}
